/**
 * CARRITO-MESAS-ADICIONES-T1, paso 2 del panel (Ares #29911; contrato de D en #29932, nexo_desktop PR 513):
 * lo que el escritorio dice de sus mesas, para que el mesero vea si la mesa que teclea ya tiene cuenta abierta.
 * Solo lectura: en este paso el pedido sale igual que hoy (la adicion a la misma cuenta es el paso 3).
 *
 * Fuente: GET /api/sync/mesas -> items [{rowId, data:{id, mesa, etiqueta, zona, estado, venta_ref, abierta_desde,
 * total, n_productos, renglones, corte}}]. Tolera camel y snake. Las horas son las del PC del escritorio, sin zona.
 *
 * @module application/carrito/mesasAbiertas
 */

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const OCUPADAS = new Set(['ocupada', 'por_cobrar']);

/** Las filas del GET en una forma. Sin filas o con forma rara: []. */
export function normalizarMesas(items) {
  return (Array.isArray(items) ? items : []).map((row) => {
    const d = row?.data ?? row ?? {};
    return {
      mesa: String(de(d, 'mesa', 'Mesa', 'id', 'Id') ?? row?.rowId ?? '').trim(),
      etiqueta: String(de(d, 'etiqueta', 'Etiqueta') ?? ''),
      estado: String(de(d, 'estado', 'Estado') ?? '').toLowerCase(),
      ventaRef: String(de(d, 'venta_ref', 'ventaRef', 'VentaRef') ?? ''),
      abiertaDesde: String(de(d, 'abierta_desde', 'abiertaDesde', 'AbiertaDesde') ?? ''),
      total: Number(de(d, 'total', 'Total')) || 0,
      nProductos: Number(de(d, 'n_productos', 'nProductos', 'NProductos')) || 0,
      corte: String(de(d, 'corte', 'Corte') ?? '')
    };
  }).filter((m) => m.mesa);
}

/** La mesa tecleada si el escritorio la tiene con cuenta abierta (ocupada o por cobrar). null si no. */
export function mesaAbierta(mesas, mesaSel) {
  if (!mesaSel || mesaSel === 'LLEVAR') return null;
  const igual = (a, b) => (/^\d+$/.test(a) && /^\d+$/.test(b) ? Number(a) === Number(b) : a === b);
  const m = (mesas || []).find((x) => igual(x.mesa, String(mesaSel)));
  return m && OCUPADAS.has(m.estado) ? m : null;
}

/** "7:40 pm" desde "yyyy-MM-ddTHH:mm:ss" (hora del PC, sin zona): se lee tal cual, sin convertir. '' si no se puede. */
export function horaCorta(iso) {
  const x = /T(\d{2}):(\d{2})/.exec(String(iso || ''));
  if (!x) return '';
  const h = Number(x[1]);
  return `${((h + 11) % 12) + 1}:${x[2]} ${h < 12 ? 'am' : 'pm'}`;
}

/** Minutos desde el corte (hora del PC) hasta ahora (la misma hora local del navegador). null si no se puede. */
export function minutosDesde(corte, ahora = new Date()) {
  const x = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(corte || ''));
  if (!x) return null;
  const t = new Date(+x[1], +x[2] - 1, +x[3], +x[4], +x[5], +(x[6] || 0));
  return Math.max(0, Math.round((ahora - t) / 60000));
}

/**
 * Lo que ve el mesero bajo el teclado de mesa: la linea de la cuenta abierta, lo que pasa con este pedido en este
 * paso, y un aviso si el dato del escritorio es viejo (mas de viejoMin minutos).
 */
export function lineaDeMesa(m, fmt, ahora = new Date(), viejoMin = 10) {
  const desde = horaCorta(m.abiertaDesde);
  const partes = [`Mesa ${m.etiqueta || m.mesa} ${m.estado === 'por_cobrar' ? 'por cobrar' : 'abierta'}${desde ? ' desde ' + desde : ''}`];
  if (m.nProductos > 0) partes.push(`${m.nProductos} producto${m.nProductos === 1 ? '' : 's'}`);
  if (m.total > 0) partes.push(fmt(m.total));
  const min = minutosDesde(m.corte, ahora);
  return {
    linea: partes.join(' · '),
    // Paso 3 (D, nexo_desktop 514): con cuenta nombrada se suma a esa cuenta; si caja la tiene abierta en Venta, se
    // suma cuando la suelte. Sin cuenta nombrada, va aparte.
    nota: m.ventaRef
      ? `Este pedido se suma a la cuenta abierta de la Mesa ${m.etiqueta || m.mesa}. Si caja la tiene abierta, se suma al cerrarla.`
      : 'Este pedido llega a caja como pedido aparte de esa cuenta.',
    viejo: min != null && min > viejoMin ? `Dato del escritorio de hace ${min} min.` : ''
  };
}

/**
 * Paso 3: las dos claves que lee el escritorio (contrato de D #29967). Con cuenta abierta y nombrada: es_adicion true y
 * venta_destino = venta_ref leida. Sin mesa abierta o sin cuenta: false y null (pedido nuevo, como siempre).
 */
export function adicionDe(m) {
  const ref = m && m.ventaRef ? m.ventaRef : null;
  return { es_adicion: !!ref, venta_destino: ref };
}
